import React, { useMemo, useState } from 'react'
import { PartnerService } from '@duck/api-definitions'
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@duck/ui'
import { Link } from '@tanstack/react-router'
import { ArrowRight, MoreVertical, RotateCcw, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTar } from '@/modules/i18n'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { useUpdateCohort } from '@/hooks/useCohorts'
import { DeleteDialog } from '../DeleteDialog/DeleteDialog'

interface CohortsOverviewTableProps {
  cohortsWithStats: PartnerService.CohortWithStats[]
  organizationId?: string
}

interface PendingClose {
  row: PartnerService.CohortWithStats
  close: () => Promise<void>
}

interface CohortRowProps {
  row: PartnerService.CohortWithStats
  organizationId?: string
  onRequestClose: (close: () => Promise<void>) => void
}

const CohortRow: React.FC<CohortRowProps> = ({ row, organizationId, onRequestClose }) => {
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const updateCohort = useUpdateCohort(row.cohort.id, organizationId)
  const isActive = row.cohort.isActive

  const handleReopen = async () => {
    try {
      await updateCohort.mutateAsync({
        name: row.cohort.name,
        description: row.cohort.description,
        organizationId: row.cohort.organizationId,
        type: row.cohort.type,
        isActive: true,
        personIds: row.cohort.personIds ?? [],
      })
      toast.success(tP('cohortReopened'))
    } catch {
      toast.error(t('errors.somethingWentWrongWeveBeenNotified'))
    }
  }

  const handleRequestClose = () => {
    onRequestClose(async () => {
      await updateCohort.mutateAsync({
        name: row.cohort.name,
        description: row.cohort.description,
        organizationId: row.cohort.organizationId,
        type: row.cohort.type,
        isActive: false,
        personIds: row.cohort.personIds ?? [],
      })
    })
  }

  return (
    <tr className="hover:bg-secondary/5">
      <td className="px-6 py-4 whitespace-nowrap">
        <Link
          to="/partner/c/$id"
          params={{ id: row.cohort.id }}
          preload="intent"
          className="text-sm font-medium text-foreground hover:text-primary hover:underline"
        >
          {row.cohort.name}
          {!isActive && (
            <span className="ml-2 text-xs text-muted-foreground font-normal">
              ({t('partner.closed')})
            </span>
          )}
        </Link>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
        {row.personCount}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
        {row.assignmentCount}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
        {row.submissionCount}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {isActive ? (
          <Badge
            variant="outline"
            className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
          >
            {tP('cohortStatusActive')}
          </Badge>
        ) : (
          <Badge variant="outline" className="border-border bg-muted text-muted-foreground">
            {tP('cohortStatusClosed')}
          </Badge>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={t('common.actions')}
              disabled={updateCohort.isPending}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link
                to="/partner/c/$id"
                params={{ id: row.cohort.id }}
                preload="intent"
              >
                <ArrowRight className="h-4 w-4 me-2" />
                {tP('goToCohort')}
              </Link>
            </DropdownMenuItem>
            {isActive ? (
              <DropdownMenuItem
                onClick={handleRequestClose}
                className="text-destructive focus:text-destructive"
              >
                <X className="h-4 w-4 me-2" />
                {tP('closeCohort')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={handleReopen}>
                <RotateCcw className="h-4 w-4 me-2" />
                {tP('reopenCohort')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  )
}

export const CohortsOverviewTable: React.FC<CohortsOverviewTableProps> = ({
  cohortsWithStats,
  organizationId,
}) => {
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const [pending, setPending] = useState<PendingClose | null>(null)

  const sortedCohorts = useMemo(() => {
    return [...cohortsWithStats].sort((a, b) => {
      if (a.cohort.isActive === b.cohort.isActive) return 0
      return a.cohort.isActive ? -1 : 1
    })
  }, [cohortsWithStats])

  const openAssignmentsCount =
    pending != null
      ? Math.max(0, pending.row.assignmentCount - pending.row.submissionCount)
      : 0

  if (cohortsWithStats.length === 0) {
    return (
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-4">
          {tP('cohortsOverview')}
        </h2>
        <p className="text-sm text-muted-foreground">{tP('noCohortsYet')}</p>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-4">
        {tP('cohortsOverview')}
      </h2>
      <div className="overflow-x-auto">
        <table className="min-w-full border border-border rounded-md divide-y divide-border">
          <thead className="bg-muted/15">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {tP('cohort')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {tP('peopleCount')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('partner.assignments')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('partner.submissions')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('common.status')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedCohorts.map((row) => (
              <CohortRow
                key={row.cohort.id}
                row={row}
                organizationId={organizationId}
                onRequestClose={(close) => setPending({ row, close })}
              />
            ))}
          </tbody>
        </table>
      </div>

      <DeleteDialog
        open={pending != null}
        onOpenChange={(open) => {
          if (!open) setPending(null)
        }}
        title={tP('closeCohort')}
        description={tP('closeCohortDescription')}
        deleteButtonText={tP('closeCohort')}
        warning={
          openAssignmentsCount > 0
            ? tP('openAssignmentsWarning', { count: openAssignmentsCount })
            : undefined
        }
        onDelete={async () => {
          if (!pending) return
          try {
            await pending.close()
            toast.success(tP('cohortClosed'))
          } catch {
            toast.error(t('errors.somethingWentWrongWeveBeenNotified'))
          }
        }}
      />
    </div>
  )
}
